import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import type { AxiosError } from 'axios';
import AppLayout from '../components/AppLayout';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import GavelIcon from '@mui/icons-material/Gavel';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { formatDate, utcMs } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSave, couldNotUpdate, couldNotFinalise, documentCopy, genericCopy } from '../utils/userFacingCopy';
import { SortableTableHeader } from '../utils/sortableTableHeader';

interface DisputeManagementDto {
  reviewId: string;
  reviewCycleId: string;
  reviewCycleName: string;
  employeeId: string;
  employeeName: string;
  managerId?: string;
  managerName?: string;
  departmentName?: string;
  disputeReason: string;
  disputedAt: string;
  disputedById: string;
  disputedByName: string;
  /** Admin notes (HR only). */
  adminComments?: string | null;
  supportingDocuments: SupportingDocument[];
  createdAt: string;
}

interface SupportingDocument {
  id: string;
  reviewId: string;
  uploadedById: string;
  uploadedByName: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  description?: string;
  uploadedAt: string;
  isEmployeeDocument: boolean;
}

export default function DisputeManagementPage() {
  const navigate = useNavigate();
  const { user, updateOpenDisputesCount, displayTimeZoneId } = useAuth();
  const canManageDisputes = user?.roles?.includes('Administrator') ?? false;
  const [disputes, setDisputes] = useState<DisputeManagementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedDispute, setSelectedDispute] = useState<DisputeManagementDto | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewNotAvailableFileName, setPreviewNotAvailableFileName] = useState<string | null>(null);
  const [finalising, setFinalising] = useState<string | null>(null);
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<{ reviewId: string; id: string; fileName: string; description: string } | null>(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<{ reviewId: string; documentId: string; fileName: string } | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [documentFilter, setDocumentFilter] = useState<string>('all'); // 'all', 'has-documents', 'no-documents'
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'employeeName' | 'reviewCycleName' | 'disputedAt' | 'documents'>('disputedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [adminCommentsEdit, setAdminCommentsEdit] = useState('');
  const [savingAdminComments, setSavingAdminComments] = useState(false);

  const loadDisputes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get<DisputeManagementDto[]>('/reviews/disputed');
      setDisputes(response.data);
      updateOpenDisputesCount(response.data?.length ?? 0);
    } catch (err: unknown) {
      const axiosError = err as AxiosError;
      const status = axiosError.response?.status;
      const message = status === 403
        ? genericCopy.disputesAdminOnly
        : getApiErrorMessage(err, couldNotLoad('disputes'));
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [updateOpenDisputesCount]);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes]);

  const handleSort = (column: 'employeeName' | 'reviewCycleName' | 'disputedAt' | 'documents') => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Get unique departments and managers for filters
  const departments = useMemo(() => {
    const depts = disputes
      .map((d) => d.departmentName)
      .filter((d): d is string => !!d);
    return Array.from(new Set(depts)).sort();
  }, [disputes]);

  const filteredAndSortedDisputes = useMemo(() => {
    let filtered = disputes;

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (dispute) =>
          dispute.employeeName.toLowerCase().includes(query) ||
          dispute.reviewCycleName.toLowerCase().includes(query) ||
          dispute.disputeReason.toLowerCase().includes(query) ||
          (dispute.departmentName && dispute.departmentName.toLowerCase().includes(query)) ||
          (dispute.managerName && dispute.managerName.toLowerCase().includes(query))
      );
    }

    // Apply document filter
    if (documentFilter === 'has-documents') {
      filtered = filtered.filter((dispute) => dispute.supportingDocuments.length > 0);
    } else if (documentFilter === 'no-documents') {
      filtered = filtered.filter((dispute) => dispute.supportingDocuments.length === 0);
    }

    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter((dispute) => dispute.departmentName === departmentFilter);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'employeeName':
          aValue = a.employeeName.toLowerCase();
          bValue = b.employeeName.toLowerCase();
          break;
        case 'reviewCycleName':
          aValue = a.reviewCycleName.toLowerCase();
          bValue = b.reviewCycleName.toLowerCase();
          break;
        case 'disputedAt':
          aValue = utcMs(a.disputedAt);
          bValue = utcMs(b.disputedAt);
          break;
        case 'documents':
          aValue = a.supportingDocuments.length;
          bValue = b.supportingDocuments.length;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [disputes, searchQuery, documentFilter, departmentFilter, sortBy, sortDirection]);

  const paginatedDisputes = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedDisputes.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedDisputes, page, rowsPerPage]);

  const handleViewReview = (reviewId: string) => {
    navigate(`/reviews/${reviewId}`);
  };

  useEffect(() => {
    if (selectedDispute) {
      setAdminCommentsEdit(selectedDispute.adminComments ?? '');
    }
  }, [selectedDispute]);

  const handleSaveAdminComments = async () => {
    if (!selectedDispute || !canManageDisputes) return;
    setSavingAdminComments(true);
    setError('');
    try {
      await apiService.put(`/reviews/${selectedDispute.reviewId}/dispute-admin-notes`, {
        adminComments: adminCommentsEdit.trim() || null,
      });
      setSelectedDispute((prev) => (prev ? { ...prev, adminComments: adminCommentsEdit.trim() || null } : null));
      const response = await apiService.get<DisputeManagementDto[]>('/reviews/disputed');
      setDisputes(response.data);
      updateOpenDisputesCount(response.data?.length ?? 0);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotSave('admin notes')));
    } finally {
      setSavingAdminComments(false);
    }
  };

  const handleManageDispute = (dispute: DisputeManagementDto) => {
    setSelectedDispute(dispute);
    setDetailDialogOpen(true);
  };

  const handleUploadDocument = async (files: File[], description: string) => {
    if (!selectedDispute) return;

    try {
      setUploading(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (description.trim()) {
          formData.append('description', description.trim());
        }
        await apiService.post(
          `/reviews/${selectedDispute.reviewId}/supporting-documents/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }
      setUploadDialogOpen(false);
      await loadDisputes();
      const response = await apiService.get<DisputeManagementDto[]>('/reviews/disputed');
      const updatedDispute = response.data.find((d) => d.reviewId === selectedDispute.reviewId);
      if (updatedDispute) {
        setSelectedDispute(updatedDispute);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.upload));
    } finally {
      setUploading(false);
    }
  };

  const handleFinaliseDispute = async (reviewId: string) => {
    try {
      setFinalising(reviewId);
      await apiService.put(`/reviews/${reviewId}/finalise-disputed`);
      await loadDisputes();
      setDetailDialogOpen(false);
      setSelectedDispute(null);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotFinalise('the dispute')));
    } finally {
      setFinalising(null);
    }
  };

  const handleDownloadDocument = async (reviewId: string, documentId: string, fileName: string) => {
    try {
      const response = await apiService.get(
        `/reviews/${reviewId}/supporting-documents/${documentId}/download`,
        {
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.download));
    }
  };

  const handleViewDocument = async (reviewId: string, documentId: string, fileName: string, contentType: string) => {
    try {
      setError('');
      const response = await apiService.get(
        `/reviews/${reviewId}/supporting-documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        setPreviewNotAvailableFileName(fileName);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.open));
    }
  };

  const handleDeleteDocument = async (reviewId: string, documentId: string, _fileName: string) => {
    try {
      setDeletingDocId(documentId);
      setError('');
      await apiService.delete(`/reviews/${reviewId}/supporting-documents/${documentId}`);
      setDeleteConfirmDoc(null);
      await loadDisputes();
      if (selectedDispute?.reviewId === reviewId) {
        const response = await apiService.get<DisputeManagementDto[]>('/reviews/disputed');
        const updated = response.data.find((d) => d.reviewId === reviewId);
        if (updated) setSelectedDispute(updated);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, documentCopy.delete));
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleUpdateDocument = async (reviewId: string, documentId: string, fileName: string, description: string) => {
    try {
      setError('');
      await apiService.patch(`/reviews/${reviewId}/supporting-documents/${documentId}`, { fileName: fileName.trim(), description: description.trim() || undefined });
      setEditDocDialogOpen(false);
      setEditingDoc(null);
      await loadDisputes();
      if (selectedDispute?.reviewId === reviewId) {
        const response = await apiService.get<DisputeManagementDto[]>('/reviews/disputed');
        const updated = response.data.find((d) => d.reviewId === reviewId);
        if (updated) setSelectedDispute(updated);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotUpdate('the document')));
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <GavelIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Dispute Management
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Manage review disputes. Upload supporting documentation and finalise disputes once resolution is complete.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Search and Filter */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Search Disputes"
                placeholder="Search by employee, review cycle, department, manager, or dispute reason..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Documents</InputLabel>
                <Select
                  value={documentFilter}
                  label="Documents"
                  onChange={(e) => {
                    setDocumentFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="has-documents">Has Documents</MenuItem>
                  <MenuItem value="no-documents">No Documents</MenuItem>
                </Select>
              </FormControl>
              {departments.length > 0 && (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={departmentFilter}
                    label="Department"
                    onChange={(e) => {
                      setDepartmentFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {(searchQuery.trim() !== '' || documentFilter !== 'all' || departmentFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => {
                      setSearchQuery('');
                      setPage(0);
                    }}
                    size="small"
                    color="primary"
                  />
                )}
                {documentFilter !== 'all' && (
                  <Chip
                    label={`Documents: ${documentFilter === 'has-documents' ? 'Has Documents' : 'No Documents'}`}
                    onDelete={() => {
                      setDocumentFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color="info"
                  />
                )}
                {departmentFilter !== 'all' && (
                  <Chip
                    label={`Department: ${departmentFilter}`}
                    onDelete={() => {
                      setDepartmentFilter('all');
                      setPage(0);
                    }}
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {filteredAndSortedDisputes.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <GavelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery.trim() ? 'No disputes match your search or filters.' : 'No active disputes'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery.trim()
                    ? 'Try adjusting your search or filter criteria.'
                    : 'All disputes have been resolved or no disputes have been raised yet.'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <SortableTableHeader
                        label="Employee"
                        active={sortBy === 'employeeName'}
                        direction={sortBy === 'employeeName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('employeeName')}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableTableHeader
                        label="Review Cycle"
                        active={sortBy === 'reviewCycleName'}
                        direction={sortBy === 'reviewCycleName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('reviewCycleName')}
                      />
                    </TableCell>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell><strong>Manager</strong></TableCell>
                    <TableCell>
                      <SortableTableHeader
                        label="Disputed At"
                        active={sortBy === 'disputedAt'}
                        direction={sortBy === 'disputedAt' ? sortDirection : 'asc'}
                        onClick={() => handleSort('disputedAt')}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableTableHeader
                        label="Documents"
                        active={sortBy === 'documents'}
                        direction={sortBy === 'documents' ? sortDirection : 'asc'}
                        onClick={() => handleSort('documents')}
                      />
                    </TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDisputes.map((dispute) => (
                    <TableRow key={dispute.reviewId} hover>
                      <TableCell>{dispute.employeeName}</TableCell>
                      <TableCell>{dispute.reviewCycleName}</TableCell>
                      <TableCell>{dispute.departmentName || '-'}</TableCell>
                      <TableCell>{dispute.managerName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatDate(dispute.disputedAt, { includeTime: true })}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={dispute.supportingDocuments.length}
                          color={dispute.supportingDocuments.length === 0 ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewReview(dispute.reviewId)}
                            color="primary"
                            title="View Review"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleManageDispute(dispute)}
                            title="Manage Dispute - Upload documents and finalise"
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAndSortedDisputes.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}

        {/* Dispute Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedDispute(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Manage Dispute
            <IconButton
              onClick={() => {
                setDetailDialogOpen(false);
                setSelectedDispute(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedDispute && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                    Employee: {selectedDispute.employeeName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Review Cycle:</strong> {selectedDispute.reviewCycleName}
                  </Typography>
                  {selectedDispute.departmentName && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Department:</strong> {selectedDispute.departmentName}
                    </Typography>
                  )}
                  {selectedDispute.managerName && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Manager:</strong> {selectedDispute.managerName}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Dispute Reason:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDispute.disputeReason}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Disputed by: {selectedDispute.disputedByName} on {formatDate(selectedDispute.disputedAt, { includeTime: true })}
                  </Typography>
                </Box>

                {canManageDisputes && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                      Admin notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      placeholder="Capture notes about this dispute (e.g. follow-up, resolution summary)..."
                      value={adminCommentsEdit}
                      onChange={(e) => setAdminCommentsEdit(e.target.value)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSaveAdminComments}
                      disabled={savingAdminComments}
                      sx={{ textTransform: 'none' }}
                    >
                      {savingAdminComments ? 'Saving...' : 'Save notes'}
                    </Button>
                    {error && (
                      <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
                        {error}
                      </Alert>
                    )}
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="600">
                      Supporting Documents ({selectedDispute.supportingDocuments.length})
                    </Typography>
                    {canManageDisputes && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        Upload Document
                      </Button>
                    )}
                  </Box>

                  {selectedDispute.supportingDocuments.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No documents uploaded. At least one document is required before finalising the dispute.
                    </Alert>
                  ) : (
                    <SupportingDocumentsTable
                      documents={selectedDispute.supportingDocuments.map((d) => ({
                        id: d.id,
                        fileName: d.fileName,
                        description: d.description,
                        uploadedByName: d.uploadedByName,
                        uploadedAt: d.uploadedAt,
                        fileSize: d.fileSize,
                        contentType: d.contentType,
                      }))}
                      onView={(docId, fileName, contentType) =>
                        handleViewDocument(selectedDispute.reviewId, docId, fileName, contentType)
                      }
                      onDownload={(docId, fileName) =>
                        handleDownloadDocument(selectedDispute.reviewId, docId, fileName)
                      }
                      onDelete={(docId, fileName) =>
                        setDeleteConfirmDoc({ reviewId: selectedDispute.reviewId, documentId: docId, fileName })
                      }
                      canEdit={canManageDisputes}
                      onEdit={(docId, fileName, description) => {
                        setEditingDoc({
                          reviewId: selectedDispute.reviewId,
                          id: docId,
                          fileName,
                          description,
                        });
                        setEditDocDialogOpen(true);
                      }}
                      formatDate={(date) => formatDate(date, { includeTime: true })}
                    />
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDetailDialogOpen(false);
                setSelectedDispute(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>
            <Button
              variant="outlined"
              onClick={() => selectedDispute && handleViewReview(selectedDispute.reviewId)}
              sx={{ textTransform: 'none' }}
            >
              View Review
            </Button>
            {canManageDisputes && selectedDispute && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleFinaliseDispute(selectedDispute.reviewId)}
                disabled={finalising === selectedDispute.reviewId || selectedDispute.supportingDocuments.length === 0}
                sx={{ textTransform: 'none' }}
              >
                {finalising === selectedDispute.reviewId ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Finalising...
                  </>
                ) : (
                  'Finalise Review'
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <DocumentUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUpload={handleUploadDocument}
          uploading={uploading}
        />

        <Dialog
          open={editDocDialogOpen}
          onClose={() => { setEditDocDialogOpen(false); setEditingDoc(null); setError(''); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Document</DialogTitle>
          <DialogContent>
            {editingDoc && (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="File name"
                  value={editingDoc.fileName}
                  onChange={(e) => setEditingDoc({ ...editingDoc, fileName: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={editingDoc.description}
                  onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
                <DialogActions sx={{ mt: 2, px: 0 }}>
                  <Button onClick={() => { setEditDocDialogOpen(false); setEditingDoc(null); setError(''); }} sx={{ textTransform: 'none' }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => editingDoc && handleUpdateDocument(editingDoc.reviewId, editingDoc.id, editingDoc.fileName, editingDoc.description)}
                    disabled={!editingDoc?.fileName.trim()}
                    sx={{ textTransform: 'none' }}
                  >
                    Save
                  </Button>
                </DialogActions>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!deleteConfirmDoc}
          onClose={() => setDeleteConfirmDoc(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Document</DialogTitle>
          <DialogContent>
            {deleteConfirmDoc && (
              <Typography variant="body1">
                Delete &quot;{deleteConfirmDoc.fileName}&quot;? This cannot be undone.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteConfirmDoc(null)} disabled={!!deletingDocId} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!!deletingDocId}
              onClick={() => deleteConfirmDoc && handleDeleteDocument(deleteConfirmDoc.reviewId, deleteConfirmDoc.documentId, deleteConfirmDoc.fileName)}
              sx={{ textTransform: 'none' }}
            >
              {deletingDocId ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!previewNotAvailableFileName} onClose={() => setPreviewNotAvailableFileName(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Preview not available</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This file type cannot be displayed in the browser. Please use Download to open it on your device.
            </Typography>
            {previewNotAvailableFileName && (
              <Typography variant="body2" sx={{ mt: 1 }} fontWeight={500}>
                File: {previewNotAvailableFileName}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewNotAvailableFileName(null)} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
